/**
 * avatarCamera — "appear as your avatar" on a live call.
 *
 * PORTED VERBATIM from the web app (HypeTunes_Web_2026/src/studio/avatarCamera.js)
 * except for the config import: the phone's calls are the same feature as the
 * studio's collab session, brokered by the same API routes, and a second copy
 * that drifts is how the two surfaces end up disagreeing about who you look
 * like. If this file needs a fix, the web's copy needs the same one.
 *
 * Wraps Reactor's X2 realtime video-editing model (docs in the API repo:
 * docs/x2_REALTIME_DEEPFAKE.md). The webcam track goes IN as X2's `source`,
 * the avatar's portrait conditions the swap as the reference image, and the
 * re-rendered `main_video` track comes back OUT — useCollabSession swaps that
 * into the peer connection in place of the raw camera.
 *
 * The API brokers the short-lived Reactor JWT (POST /api/collab/reactor-token)
 * so the Reactor API key never reaches the browser; the same call resolves the
 * avatar's signed reference-image URL (fetch → blob → uploadFile, the exact
 * flow LiveWorldExperience uses for Helios).
 *
 * Two timing traps this file exists to absorb:
 *  - connect() resolves when the SESSION exists, before the WebRTC transport
 *    reaches "ready" — publishing then throws `Cannot publish track "source"
 *    - not connected`. We wait on getStatus()/statusChanged first.
 *  - the `main_video` track OBJECT arrives at connection time, long before
 *    frames flow. Announcing it immediately swaps a black track into the call
 *    — we announce only on real media (track `unmute` / generation_started).
 */
import { X2Model } from '@reactor-models/x2';
import { API_BASE } from '../config';

// The doc's canonical character-swap instruction, tightened for a video call:
// hold the swap steady, let the human's performance drive it, and never let
// the real person surface.
const X2_SWAP_PROMPT =
  'Replace the person with the character from the reference image. '
  + 'The character must be fully present in every frame — never show the original '
  + "person's face or body at any point. "
  + 'Keep the framing, pose, expressions and movements of the original person. '
  + 'Natural video-call lighting.';

const READY_TIMEOUT_MS = 20000;
// First blocks out of the model can still be settling into the swap. Hold the
// placeholder a touch longer and hand over a stabilized picture — the peer
// must never see a half-formed morph (or worse, a recognizable real face).
const WARMUP_MS = 2500;

/** connect() resolves at "session created" — block until the transport is usable. */
function waitUntilReady(x2) {
  if (x2.getStatus?.() === 'ready') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Timed out connecting to the avatar renderer.')),
      READY_TIMEOUT_MS
    );
    x2.on('statusChanged', (status) => {
      if (status === 'ready') { clearTimeout(timer); resolve(); }
      else if (status === 'disconnected') {
        clearTimeout(timer);
        reject(new Error('The avatar renderer disconnected during setup.'));
      }
    });
  });
}

/**
 * Start the avatar camera.
 * @param {object} opts
 * @param {string} opts.token        HypeTunes user JWT (for the token broker)
 * @param {number} opts.avatarId     avatar to appear as (must be the user's own)
 * @param {MediaStreamTrack} opts.sourceTrack  the raw webcam video track
 * @param {(stream: MediaStream, track: MediaStreamTrack) => void} opts.onOutput
 *        called once real avatar frames are flowing (not on bare track arrival)
 * @param {() => void} [opts.onEnded]  called when a LIVE output dies (token
 *        expiry, model stop) — the caller should fall back to the raw camera
 * @returns {Promise<{ stop: () => void }>}
 */
export async function startAvatarCamera({ token, avatarId, sourceTrack, onOutput, onEnded }) {
  const r = await fetch(`${API_BASE}/api/collab/reactor-token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ avatarId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.jwt) throw new Error(d.error || 'The avatar camera is unavailable right now.');

  const x2 = new X2Model();
  let stopped = false;

  // Hold the output until media actually flows, then WARM UP before handing it
  // over exactly once — the swap goes on the wire stabilized, not mid-morph.
  let outTrack = null;
  let outStream = null;
  let announced = false;
  let warmupTimer = 0;
  const announce = () => {
    if (stopped || announced || !outTrack) return;
    announced = true;
    onOutput(outStream, outTrack);
  };
  const scheduleAnnounce = () => {
    if (stopped || announced || warmupTimer || !outTrack) return;
    warmupTimer = setTimeout(announce, WARMUP_MS);
  };
  x2.onMainVideo((track, stream) => {
    if (stopped) return;
    outTrack = track;
    outStream = stream;
    // Only a track that was LIVE dying is a fallback event — a never-started
    // track ending is just teardown noise.
    track.addEventListener?.('ended', () => { if (!stopped && announced) onEnded?.(); });
    if (track.muted === false) scheduleAnnounce();
    else track.addEventListener?.('unmute', scheduleAnnounce, { once: true });
  });
  x2.onGenerationStarted?.(() => scheduleAnnounce());

  await x2.connect(d.jwt);
  try {
    await waitUntilReady(x2);
    // Belt and braces: the transport can still be settling right at "ready".
    let publishErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await x2.publishSource(sourceTrack);
        publishErr = null;
        break;
      } catch (err) {
        publishErr = err;
        await new Promise((res) => setTimeout(res, 600 * (attempt + 1)));
      }
    }
    if (publishErr) throw publishErr;

    // Via our API, not GCS — the artist-images bucket has no browser CORS.
    const imgRes = await fetch(`${API_BASE}/api/collab/avatar-image/${avatarId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!imgRes.ok) throw new Error('Could not load the avatar reference image.');
    const blob = await imgRes.blob();
    const fileRef = await x2.uploadFile(blob);
    await x2.setReferenceImage({ reference_image: fileRef });
    await x2.setPrompt({ prompt: X2_SWAP_PROMPT });
  } catch (err) {
    stopped = true;
    try { x2.disconnect(); } catch { /* already down */ }
    throw err;
  }

  return {
    stop() {
      if (stopped) return;
      stopped = true;
      clearTimeout(warmupTimer);
      try { x2.unpublishSource(); } catch { /* already down */ }
      try { x2.disconnect(); } catch { /* already down */ }
    },
  };
}
